import {
	Link,
	useLocation,
	useNavigate,
	useOutletContext,
} from 'react-router-dom';
import { useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Users } from 'lucide-react';
// import { Filter } from 'lucide-react';
import { Button } from './Button';
import { ProfessionalHeaderProfileMenu } from './ProfessionalHeaderProfileMenu';
import { ProfessionalHeaderCalendarOptionsMenu } from './ProfessionalHeaderCalendarOptionsMenu';
import { ProfessionalHeaderQuickActionsMenu } from './ProfessionalHeaderQuickActionsMenu';
import { ProfessionalHeaderCalendarViewPill } from './ProfessionalHeaderCalendarViewPill';
import { cn } from '@/lib/utils';
import {
	professionalHeaderControlHeightClass,
	professionalHeaderControlPaddingClass,
	professionalHeaderControlTextClass,
	professionalHeaderActionIconClass,
	professionalHeaderHeightClass,
	// professionalHeaderIconOnlyButtonClass,
} from './professionalHeaderConstants';
import { getProfessionalData } from '@/utils/cookies';
import { useProfessionalCalendar } from '@/context/ProfessionalCalendarContext';
import { encodeUrl } from '@/utils/encodeUrl';
import { professionalHasP2PPermission } from '@/utils/professionalPermissionTags';

type ProfessionalLayoutContext = {
	data: Professionals.Professional | undefined;
	isLoading: boolean;
};

const PROFESSIONAL_HEADER_LOGO =
	'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986601/Plumaa_H_psjyrs.png';

const PROFESSIONAL_HEADER_LOGO_COMPACT =
	'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986601/Icon_dz4cwq.png';

const professionalHeaderPillHeight = professionalHeaderControlHeightClass;
const professionalHeaderPillText = professionalHeaderControlTextClass;
const professionalHeaderPillPadding = professionalHeaderControlPaddingClass;

const professionalHeaderGroupedPillClass = cn(
	'flex items-stretch gap-0.5 rounded-full border border-zinc-200 p-0.5',
	professionalHeaderPillHeight,
);

const ProfessionalHeaderCalendarViewToggle = () => (
	<ProfessionalHeaderCalendarViewPill className="hidden min-[1050px]:flex" />
);

const ProfessionalHeaderCalendarControls = () => {
	const {
		handleToday,
		handlePrev,
		handleNext,
		nextDisabled,
		monthName,
		year,
	} = useProfessionalCalendar();

	const navButtonClass =
		'flex h-full shrink-0 items-center justify-center rounded-full transition-colors outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300/70';

	const actionButtonClass = cn(
		'inline-flex items-center justify-center rounded-full border border-zinc-200 bg-white py-0 transition-colors focus:outline-none',
		professionalHeaderPillHeight,
		professionalHeaderPillPadding,
		professionalHeaderPillText,
	);

	return (
		<>
			<button
				type="button"
				onClick={handleToday}
				className={cn(
					actionButtonClass,
					'text-zinc-700 hover:bg-zinc-50',
				)}
			>
				Hoje
			</button>

			<div
				className={cn(professionalHeaderGroupedPillClass, 'bg-white')}
				role="group"
				aria-label="Navegar período"
			>
				<button
					type="button"
					onClick={handlePrev}
					className={cn(
						navButtonClass,
						'w-8 hover:bg-zinc-50 md:w-9',
					)}
					aria-label="Período anterior"
				>
					<ChevronLeft size={20} className="text-zinc-500" />
				</button>
				<button
					type="button"
					onClick={handleNext}
					disabled={nextDisabled}
					className={cn(
						navButtonClass,
						'w-8 md:w-9',
						nextDisabled
							? 'cursor-not-allowed text-zinc-300 opacity-40'
							: 'text-zinc-500 hover:bg-zinc-50',
					)}
					aria-label="Próximo período"
				>
					<ChevronRight size={20} />
				</button>
			</div>

			<div className="flex min-w-0 items-baseline gap-2">
				<span className="truncate text-base font-bold capitalize leading-tight text-zinc-800 md:text-xl">
					{monthName}
					<span className="hidden min-[1050px]:inline">{` ${year}`}</span>
				</span>
			</div>
		</>
	);
};

const ProfessionalHeader = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const isHomePage = location.pathname === '/profissional/home';
	const { data: professionalFromContext } =
		useOutletContext<ProfessionalLayoutContext | null>() ?? {};
	const professional = professionalFromContext ?? getProfessionalData();
	const canManagePatients = professionalHasP2PPermission(professional);
	const { setHistoryPanelOpen } = useProfessionalCalendar();
	// const { toggleHistoryPanel, isHistoryPanelOpen, setHistoryPanelOpen } = useProfessionalCalendar();

	useEffect(() => {
		if (!isHomePage) setHistoryPanelOpen(false);
	}, [isHomePage, setHistoryPanelOpen]);

	const buildAgendarParams = () => {
		if (!professional?.id) return null;

		const serviceId =
			professional.professional_services?.[0]?.service?.id ?? '';

		return encodeUrl({
			s: '1',
			professional_id: professional.id,
			service_id: serviceId,
			user_ids: '[]',
		});
	};

	const handleCreateSession = () => {
		const encodedParams = buildAgendarParams();
		if (!encodedParams) return;
		navigate(`/profissional/consultas/agendar?d=${encodedParams}`);
	};

	const actionButtonClass = cn(
		'inline-flex items-center justify-center gap-1.5 rounded-full border border-zinc-200 py-0 transition-colors focus:outline-none',
		professionalHeaderPillHeight,
		professionalHeaderPillPadding,
		professionalHeaderPillText,
	);

	return (
		<header
			className={cn(
				'relative z-50 flex shrink-0 items-center gap-2 border-b border-stone-200 bg-white px-4 md:gap-4',
				professionalHeaderHeightClass,
			)}
		>
			<button
				type="button"
				onClick={() => navigate('/profissional/home')}
				className="shrink-0 rounded-md transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
				aria-label="Ir para a home"
			>
				<img
					src={PROFESSIONAL_HEADER_LOGO_COMPACT}
					className="h-8 w-auto object-contain md:h-10 min-[1050px]:hidden"
					alt="Logotipo Plumaa"
				/>
				<img
					src={PROFESSIONAL_HEADER_LOGO}
					className="hidden h-8 w-auto object-contain md:h-10 min-[1050px]:block"
					alt="Logotipo Plumaa"
				/>
			</button>

			{isHomePage ? (
				<div className="flex min-w-0 flex-1 items-center gap-2 md:gap-4">
					<ProfessionalHeaderCalendarControls />
				</div>
			) : (
				<div className="min-w-0 flex-1" />
			)}

			{isHomePage ? (
				<>
					{canManagePatients ? (
						<Link
							to="/profissional/pacientes"
							aria-label="Ver pacientes"
							className={cn(
								actionButtonClass,
								'hidden bg-white text-zinc-700 hover:bg-zinc-50 min-[700px]:inline-flex',
							)}
						>
							<Users
								className={cn(
									professionalHeaderActionIconClass,
									'text-zinc-600',
								)}
							/>
							Pacientes
						</Link>
					) : null}

					{canManagePatients ? (
						<>
							<Button
								text="Nova consulta"
								variant="contained"
								size="small"
								className={cn(
									actionButtonClass,
									'hidden border-0 bg-brand-purple font-bold text-white shadow-sm hover:bg-brand-purple/90 min-[700px]:inline-flex',
								)}
								startIcon={
									<Plus
										className={cn(
											professionalHeaderActionIconClass,
											'stroke-[3] text-white',
										)}
									/>
								}
								onClick={handleCreateSession}
							/>

							<ProfessionalHeaderQuickActionsMenu
								onCreateSession={handleCreateSession}
							/>
						</>
					) : null}

					<ProfessionalHeaderCalendarViewToggle />

					<ProfessionalHeaderCalendarOptionsMenu />

					{/* Botão de filtro oculto temporariamente
          <button
            type="button"
            onClick={toggleHistoryPanel}
            aria-label="Abrir filtros"
            aria-expanded={isHistoryPanelOpen}
            className={cn(
              professionalHeaderIconOnlyButtonClass,
              'hidden min-[1050px]:inline-flex',
              isHistoryPanelOpen && 'bg-stone-100 hover:bg-stone-100',
            )}
          >
            <Filter
              className={cn(
                professionalHeaderActionIconClass,
                'text-stone-700 transition-[fill]',
              )}
              fill={isHistoryPanelOpen ? 'currentColor' : 'none'}
              strokeWidth={2}
            />
          </button>
          */}
				</>
			) : null}

			<ProfessionalHeaderProfileMenu />
		</header>
	);
};

export { ProfessionalHeader };
